// File Metadata:
// Filename: userSlice.js
// Version: 1.04
// Created: 2025-03-19 23:18:38
// Last Modified: 2025-06-16
// Authors: Dieuwer Descheemaecker, GitHub Copilot
// Description: Enhanced user slice with detailed error handling, profile update actions, and points management

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../api/axiosInstance';

export const loginUser = createAsyncThunk('user/login', async ({ email, password, captchaToken }, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post('/auth/login', { email, password, captchaToken });
    // Store the access token in localStorage
    localStorage.setItem('token', response.data.token);
    return response.data.user;
  } catch (err) {
    return rejectWithValue(err.response?.data || { message: 'Login failed' });
  }
});

export const loadUser = createAsyncThunk('user/load', async (_, { rejectWithValue }) => {
  try {
    // axiosInstance automatically adds the token header
    const response = await axiosInstance.get('/auth/me');
    return response.data;
  } catch (err) {
    localStorage.removeItem('token'); // Remove invalid token
    return rejectWithValue(err.response?.data || { message: 'Failed to load user data' });
  }
});

export const refreshToken = createAsyncThunk(
  'user/refreshToken',
  async (_, { rejectWithValue }) => {
    console.log('=== refreshToken action called ===');
    
    // We can't check for HttpOnly cookies via document.cookie - they're not accessible to JavaScript
    // But they ARE automatically sent with requests to the same domain
    console.log('Attempting refresh request (HttpOnly cookie will be sent automatically if it exists)...');
    
    try {
      console.log('Making refresh request...');
      // axiosInstance automatically sends cookies with the request
      const response = await axiosInstance.post('/auth/refresh');
      
      console.log('Refresh response received:', response.status);
      console.log('Refresh response data:', response.data);
      
      // Store new token
      console.log('Storing new access token in localStorage...');
      localStorage.setItem('token', response.data.accessToken);
      
      return {
        accessToken: response.data.accessToken,
        user: response.data.user
      };
    } catch (err) {
      console.log('Token refresh error:', err.response?.status, err.response?.data);
      
      // Remove the token if refresh failed
      localStorage.removeItem('token');
      
      return rejectWithValue({
        message: err.response?.data?.error || 'Token refresh failed',
        status: err.response?.status,
        error: true
      });
    }
  }
);

// Async thunk for incrementing current user's points
export const incrementUserPoints = createAsyncThunk(
  'user/incrementUserPoints',
  async ({ points, reason }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.patch('/profile/points/increment', { points, reason });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update points');
    }
  }
);

// Async thunk for setting absolute points
export const setUserPoints = createAsyncThunk(
  'user/setUserPoints',
  async (points, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put('/profile/points', { points });
      return response.data.points;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to set points');
    }
  }
);

const userSlice = createSlice({
  name: 'user',
  initialState: { 
    data: null, 
    token: localStorage.getItem('token'),
    status: 'idle', 
    error: null 
  },reducers: {    
    logout: (state) => {
      state.data = null;
      state.token = null;
      state.error = null;
      localStorage.removeItem('token');

      // Optional: Invalidate token on the server
      axiosInstance.post('/auth/logout').catch((err) => {
        console.error('Failed to invalidate token on server:', err);
      });
    },
    setCredentials: (state, action) => {
      const { accessToken, user } = action.payload;
      state.data = user;
      state.token = accessToken;
      state.status = 'succeeded';
      state.error = null;
      // Store token in localStorage
      if (accessToken) {
        localStorage.setItem('token', accessToken);
      }
    },
    updatePoints: (state, action) => {
      if (state.data) {
        state.data.points = (state.data.points || 0) + action.payload;
      }
    },
    setPoints: (state, action) => {
      if (state.data) {
        state.data.points = action.payload;
      }
    },
    setUserData: (state, action) => {
      state.data = action.payload;
      state.status = 'succeeded';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.data = action.payload;
        state.status = 'succeeded';
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.error = action.payload; // Full error object from backend
        state.status = 'failed';
      })
      .addCase(loadUser.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(loadUser.fulfilled, (state, action) => {
        state.data = action.payload;
        state.status = 'succeeded';
      })      .addCase(loadUser.rejected, (state, action) => {
        state.data = null;
        state.error = action.payload.message;
        state.status = 'idle';
      })
      .addCase(refreshToken.fulfilled, (state, action) => {
        state.status = 'succeeded';
      })
      .addCase(refreshToken.rejected, (state, action) => {
        state.error = action.payload.message;
        state.status = 'failed';
      })
      .addCase(incrementUserPoints.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(incrementUserPoints.fulfilled, (state, action) => {
        state.status = 'succeeded';
        if (state.data) {
          state.data.points = action.payload.user.points;
        }
      })
      .addCase(incrementUserPoints.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      .addCase(setUserPoints.fulfilled, (state, action) => {
        if (state.data) {
          state.data.points = action.payload;
        }
      });
  },
});

export const { logout, setCredentials, updatePoints, setPoints, setUserData } = userSlice.actions;

export const setupTokenRefresh = (dispatch) => {
  const token = localStorage.getItem('token');
  if (!token) return;

  // Note: We can't check for HttpOnly cookies via JavaScript
  // We'll just set up the refresh timer assuming the refresh cookie exists
  // The actual refresh request will succeed or fail based on whether the cookie is present
  
  const { exp } = JSON.parse(atob(token.split('.')[1])); // Decode JWT payload
  const timeToRefresh = exp * 1000 - Date.now() - 60000; // Refresh 1 minute before expiration
  
  if (timeToRefresh > 0) {
    setTimeout(() => {
      dispatch(refreshToken());
    }, timeToRefresh);
  }
};

export default userSlice.reducer;